import * as React from 'react';

import { cn } from '../../../lib/stylingUtils';

import EmptyContainerSvg from './EmptyContainer.svg';

export interface EmptyProps {
  /**
   * The main title/heading for the empty state
   */
  title?: string;

  /**
   * The description text below the title
   */
  description?: string | React.ReactNode;

  /**
   * An optional icon to display above the title
   */
  icon?: React.JSX.Element;

  /**
   * Optional action button to display below the description
   */
  action?: React.JSX.Element;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Whether to center the content vertically and horizontally
   */
  centered?: boolean;

  size?: 'sm' | 'md' | 'lg';
  style?: React.CSSProperties;
}

export const Empty = ({
  title = 'No Data',
  description,
  icon,
  action,
  className,
  centered = true,
  size = 'lg',
  style,
}: EmptyProps) => {
  const containerClasses = cn(
    'flex flex-col items-center justify-center p-6',
    centered && 'h-full',
    className,
  );

  return (
    <div className={cn('@Empty w-full', containerClasses)} style={style}>
      <div className="text-neutral/40">
        {icon ?? (
          <img
            src={EmptyContainerSvg}
            alt="Empty state"
            className={cn('size-24', size === 'sm' && 'size-16')}
          />
        )}
      </div>

      <div className="flex flex-col items-center justify-center">
        <div
          className={cn(
            'mt-[0.5em] mb-[0.2em] text-lg font-medium text-neutral',
            size === 'sm' && 'text-sm',
            size === 'md' && 'text-base',
            size === 'lg' && 'text-lg',
          )}
        >
          {title}
        </div>
        {description && (
          <div
            className={cn(
              'mb-4 max-w-120 text-center text-sm text-neutral/50',
              size === 'sm' && 'text-xs',
              size === 'md' && 'text-sm',
              size === 'lg' && 'text-base',
            )}
          >
            {typeof description === 'string' ? (
              <p>{description}</p>
            ) : (
              description
            )}
          </div>
        )}

        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
};

export type MaybeEmptyProps = {
  children: React.ReactNode;
  isEmpty: boolean;
};

export const MaybeEmpty = ({
  children,
  isEmpty,
  ...emptyProps
}: MaybeEmptyProps & EmptyProps) =>
  isEmpty ? <Empty {...emptyProps} /> : children;
